/**
 * Shell: navigation, the operator login, and the mock-data honesty banner.
 *
 * Access is a real operator session (T-029): username and password against
 * `POST /operator/login`, a short-lived bearer token, actions attributed to
 * the signed-in operator in the append-only audit log. The mock banner stays
 * until every screen runs live — an internal tool that quietly shows invented
 * figures is worse than one that shows none.
 *
 * The screen registry lives in `nav.tsx` (the B4 nav split): a wave adding a
 * panel page edits that file and its own route component, never this shell.
 */
import { useEffect, useState, type ReactNode } from 'react';
import { MOCK_MODE, confirmMfa, enrollMfa, fetchOverview } from './api/client.ts';
import {
  currentMfa,
  currentOperator,
  hasCredential,
  login,
  markMfaEnrolled,
  setSessionEndedListener,
  signOut,
  TotpNeeded,
} from './api/auth.ts';
import type { MfaEnrollment } from './api/types.ts';
import { navigate, useRoute } from './lib/router.ts';
import { useAsync } from './lib/useAsync.ts';
import { FeedbackProvider } from './ui/feedback.tsx';
import { Chip } from './ui/primitives.tsx';
import { BrandMark, IconLock, IconPlug, IconPulse, IconShield } from './ui/icons.tsx';
import { NAV, SCREENS, visibleTo, type Screen } from './nav.tsx';

export function App(): ReactNode {
  const route = useRoute();
  const [authed, setAuthed] = useState(hasCredential);
  const screen = SCREENS.find((s) => s.id === route) ?? SCREENS[0]!;

  // An expired or revoked session drops the shell back to the login rather
  // than leaving every screen erroring behind a stale token.
  useEffect(() => {
    setSessionEndedListener(() => setAuthed(false));
    return () => setSessionEndedListener(null);
  }, []);

  if (!authed && !MOCK_MODE) {
    return <LoginGate onDone={() => setAuthed(true)} />;
  }

  return (
    <FeedbackProvider>
      <Shell screen={screen} />
    </FeedbackProvider>
  );
}

function Shell({ screen }: { screen: Screen }): ReactNode {
  const operator = currentOperator();
  const [mfa, setMfa] = useState(currentMfa());
  const [mfaOpen, setMfaOpen] = useState(false);
  // The queue counters. A failure here is silent by design: a badge that cannot
  // be fetched simply does not appear, and no screen is blocked by it.
  const overview = useAsync(() => fetchOverview('24h'), []);
  const queues = {
    deposits: overview.data?.pendingDeposits ?? 0,
    withdrawals: overview.data?.pendingWithdrawals ?? 0,
  };

  return (
    <div className="shell">
      <nav className="sidebar">
        <div className="brand">
          <BrandMark />
          <span className="brand__text">
            <span className="brand__name">Operator</span>
            <span className="brand__tag">Internal</span>
          </span>
        </div>

        {NAV.map((group) => visibleTo(group, operator?.role).length === 0 ? null : (
          <div className="nav-group" key={group.label}>
            <span className="nav-group__label">{group.label}</span>
            {visibleTo(group, operator?.role).map((s) => {
              const waiting = s.queue ? queues[s.queue] : 0;
              const active = s.id === screen.id;
              return (
                <a
                  key={s.id}
                  href={`#/${s.id}`}
                  className={`sidebar__link${active ? ' is-active' : ''}`}
                  aria-current={active ? 'page' : undefined}
                  onClick={(event) => {
                    event.preventDefault();
                    navigate(s.id);
                  }}
                >
                  {active ? <span className="sidebar__marker" /> : null}
                  {s.icon}
                  <span className="sidebar__label">{s.label}</span>
                  {waiting > 0 ? (
                    <span className="sidebar__count" title={`${waiting} waiting`}>
                      {waiting}
                    </span>
                  ) : null}
                </a>
              );
            })}
          </div>
        ))}

        <div className="sidebar__foot">
          <span className={`source ${MOCK_MODE ? 'source--mock' : 'source--live'}`}>
            <span className="source__dot" />
            {MOCK_MODE ? 'Mock data · VITE_ADMIN_MOCK=1' : 'Live API'}
          </span>
        </div>
      </nav>

      <main className="main">
        <header className="topbar">
          <span className="topbar__heading">
            <h1 className="topbar__title">{screen.title}</h1>
            <span className="topbar__sub">{screen.sub}</span>
          </span>
          <span className="topbar__spacer" />
          <Chip icon={MOCK_MODE ? <IconPlug size="sm" /> : <IconPulse size="sm" />}>
            {MOCK_MODE ? 'Mock' : 'Live'}
          </Chip>
          {operator ? (
            <Chip icon={<IconShield size="sm" />}>
              {operator.username} · {operator.role}
            </Chip>
          ) : null}
          {operator && !MOCK_MODE ? (
            mfa.enrolled ? (
              <Chip icon={<IconLock size="sm" />}>2FA on</Chip>
            ) : (
              <button
                type="button"
                className={`btn btn--sm ${mfa.enrollmentRequired ? 'btn--primary' : 'btn--ghost'}`}
                onClick={() => setMfaOpen(true)}
                title="Enrol a second factor for your operator account"
              >
                <IconLock size="sm" />
                {mfa.enrollmentRequired ? 'Enable 2FA (required)' : 'Enable 2FA'}
              </button>
            )
          ) : null}
          {MOCK_MODE ? (
            <button type="button" className="btn btn--sm btn--ghost" onClick={() => setMfaOpen(true)}>
              <IconLock size="sm" />
              Enable 2FA
            </button>
          ) : null}
          {operator ? (
            <button type="button" className="btn btn--sm btn--ghost" onClick={signOut}>
              <IconLock size="sm" />
              Sign out
            </button>
          ) : null}
        </header>
        {mfaOpen ? (
          <MfaModal
            onClose={() => setMfaOpen(false)}
            onEnrolled={() => {
              markMfaEnrolled();
              setMfa(currentMfa());
              setMfaOpen(false);
            }}
          />
        ) : null}

        <div className="content">
          <div className="content__inner">
            {screen.render()}
          </div>
        </div>
      </main>
    </div>
  );
}

/**
 * Operator sign-in. Every action taken past this gate is attributed to this
 * account in the append-only audit log, so the session is personal: no shared
 * credentials, and the token lives in memory for this tab only.
 */
function LoginGate({ onDone }: { onDone: () => void }): ReactNode {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [needCode, setNeedCode] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(): Promise<void> {
    if (busy || username.trim() === '' || password === '') return;
    if (needCode && !/^\d{6}$/.test(totpCode.trim())) return;
    setBusy(true);
    setError(null);
    try {
      await login(username.trim(), password, needCode ? totpCode.trim() : undefined);
      onDone();
    } catch (cause) {
      if (cause instanceof TotpNeeded) {
        // The account is enrolled — reveal the code field rather than treating
        // it as a bad password (ADR-046).
        setNeedCode(true);
        setError(cause.invalid ? cause.message : null);
      } else {
        setError(cause instanceof Error ? cause.message : 'Sign in failed.');
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="scrim">
      <form
        className="modal"
        onSubmit={(event) => {
          event.preventDefault();
          void onSubmit();
        }}
      >
        <div className="modal__head">
          <span className="modal__icon">
            <IconShield size="lg" />
          </span>
          <div>
            <h2 className="modal__title">Operator sign in</h2>
          </div>
        </div>
        <div className="modal__body">
          Actions here release real money and are recorded against your account in the audit log.
          The session is held in memory for this tab only — closing it signs you out.
        </div>
        <label className="field">
          <span className="field__label">Username</span>
          <input
            className="input"
            value={username}
            autoFocus
            autoComplete="username"
            onChange={(event) => setUsername(event.target.value)}
          />
        </label>
        <label className="field">
          <span className="field__label">Password</span>
          <input
            className="input"
            type="password"
            value={password}
            autoComplete="current-password"
            onChange={(event) => setPassword(event.target.value)}
          />
          {error && !needCode ? <span className="field__error">{error}</span> : null}
        </label>
        {needCode ? (
          <label className="field">
            <span className="field__label">Authenticator code</span>
            <input
              className="input"
              inputMode="numeric"
              autoFocus
              maxLength={6}
              autoComplete="one-time-code"
              placeholder="6-digit code"
              value={totpCode}
              onChange={(event) => setTotpCode(event.target.value.replace(/\D/g, ''))}
            />
            <span className="field__hint">
              This account has two-factor enabled. Enter the current code from your authenticator app.
            </span>
            {error ? <span className="field__error">{error}</span> : null}
          </label>
        ) : null}
        <div className="modal__actions">
          <button
            type="submit"
            className="btn btn--primary"
            disabled={busy || username.trim() === '' || password === '' || (needCode && !/^\d{6}$/.test(totpCode.trim()))}
          >
            {busy ? 'Signing in…' : needCode ? 'Verify and sign in' : 'Sign in'}
          </button>
        </div>
      </form>
    </div>
  );
}

/**
 * Operator TOTP enrollment (ADR-046). Two steps in one modal: enroll to get a
 * secret and an otpauth:// URI, then confirm one live code to enable it. The
 * secret is shown once, for manual entry into an authenticator app; nothing is
 * stored in the browser. Recovery is a box-access seed-script path, never here.
 */
function MfaModal({ onClose, onEnrolled }: { onClose: () => void; onEnrolled: () => void }): ReactNode {
  const [enrollment, setEnrollment] = useState<MfaEnrollment | null>(null);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    setBusy(true);
    enrollMfa()
      .then((e) => { if (live) setEnrollment(e); })
      .catch((cause) => { if (live) setError(cause instanceof Error ? cause.message : 'Could not begin enrollment.'); })
      .finally(() => { if (live) setBusy(false); });
    return () => { live = false; };
  }, []);

  async function confirm(): Promise<void> {
    if (busy || !/^\d{6}$/.test(code.trim())) return;
    setBusy(true);
    setError(null);
    try {
      await confirmMfa(code.trim());
      onEnrolled();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'That code was not accepted.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="scrim" onClick={onClose}>
      <form
        className="modal"
        onClick={(event) => event.stopPropagation()}
        onSubmit={(event) => { event.preventDefault(); void confirm(); }}
      >
        <div className="modal__head">
          <span className="modal__icon"><IconLock size="lg" /></span>
          <div>
            <h2 className="modal__title">Enable two-factor</h2>
          </div>
        </div>
        <div className="modal__body">
          Add your operator account to an authenticator app, then enter the current code to turn on
          two-factor. Approver and admin accounts are required to have it. If you lose your
          authenticator, an admin resets it from the server — there is no self-service reset.
        </div>
        {enrollment ? (
          <>
            <label className="field">
              <span className="field__label">Secret (manual entry)</span>
              <input className="input mono-code" readOnly value={enrollment.secret} onFocus={(e) => e.target.select()} />
              <span className="field__hint">Or open this in your app: <span className="mono-code">{enrollment.uri}</span></span>
            </label>
            <label className="field">
              <span className="field__label">Code from your app</span>
              <input
                className="input"
                inputMode="numeric"
                autoFocus
                maxLength={6}
                placeholder="6-digit code"
                value={code}
                onChange={(event) => setCode(event.target.value.replace(/\D/g, ''))}
              />
              {error ? <span className="field__error">{error}</span> : null}
            </label>
          </>
        ) : (
          <p className="field__hint">{error ?? 'Generating your enrollment secret…'}</p>
        )}
        <div className="modal__actions">
          <button type="button" className="btn btn--ghost btn--sm" onClick={onClose}>Cancel</button>
          <button
            type="submit"
            className="btn btn--primary"
            disabled={busy || !enrollment || !/^\d{6}$/.test(code.trim())}
          >
            {busy ? 'Working…' : 'Verify and enable'}
          </button>
        </div>
      </form>
    </div>
  );
}
